import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Select, message, Spin, Row, Col, Statistic, Progress } from 'antd';
import { ReloadOutlined, CalculatorOutlined, PlayCircleOutlined, StopOutlined } from '@ant-design/icons';
import moment from 'moment';
import { formatIndianNumber } from '../../../utils/numberFormat';
import { getApiEndpoint } from '../../../config/apiBase';

const { Option } = Select;

const RuntimeCountDemo = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counting, setCounting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState('2025-09');
  const [selectedUser, setSelectedUser] = useState('all'); // 'all' means all users
  const [users, setUsers] = useState([{ user_id: 'all', user_name: 'All Users' }]);

  // Simulate runtime count with progress
  const handleRuntimeCount = async () => {
    setCounting(true);
    setProgress(0);
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 200);

    try {
      // Simulate API call to trigger aggregation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Complete progress
      clearInterval(progressInterval);
      setProgress(100);
      
      // Fetch updated data
      await fetchData();
      
      message.success('Runtime count completed successfully!');
    } catch (error) {
      message.error('Failed to complete runtime count');
    } finally {
      setCounting(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  // Fetch users for dropdown
  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch data when month or user changes
  useEffect(() => {
    if (selectedMonth) {
      fetchData();
    }
  }, [selectedMonth, selectedUser]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(getApiEndpoint('/migration/sms-summary/users'));
      if (response.ok) {
        const result = await response.json();
        // Add "All Users" option at the beginning with a special value
        const allUsersOption = { user_id: 'all', user_name: 'All Users' };
        setUsers([allUsersOption, ...(result.data || [])]);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Fallback to mock users with All Users option
      setUsers([
        { user_id: 'all', user_name: 'All Users' },
        { user_id: 9, user_name: 'nimich' },
        { user_id: 10, user_name: 'user2' },
        { user_id: 11, user_name: 'user3' },
      ]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build URL with filters
      let url = getApiEndpoint('/migration/sms-summary/data') + '?';
      const params = new URLSearchParams();
      
      // Set date range for the selected month
      const startDate = `${selectedMonth}-01`;
      const endDate = `${selectedMonth}-${moment(selectedMonth, 'YYYY-MM').daysInMonth()}`;
      
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      params.append('limit', '500');
      
      // Add user filter if selected (and not 'all')
      if (selectedUser && selectedUser !== 'all') {
        params.append('user_id', selectedUser);
      }
      
      url += params.toString();
      
      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        
        // Transform the data to match the table structure
        const transformedData = (result.data || []).map(item => ({
          id: item.id,
          user_id: item.user_id,
          user_name: item.user_name,
          report_month: item.report_date ? item.report_date.substring(0, 7) : selectedMonth,
          sms_count: parseInt(item.sms_unit) || 0,
          sent: parseInt(item.sent_count) || 0,
          delivered: parseInt(item.delivered_count) || 0,
          failed: parseInt(item.failed_count) || 0,
          pending: parseInt(item.pending_count) || 0,
        }));
        
        // Group data by user and sum the values
        const groupedData = transformedData.reduce((acc, item) => {
          const existingUser = acc.find(u => u.user_id === item.user_id);
          
          if (existingUser) {
            existingUser.sms_count += item.sms_count;
            existingUser.sent += item.sent;
            existingUser.delivered += item.delivered;
            existingUser.failed += item.failed;
            existingUser.pending += item.pending;
          } else {
            acc.push({
              ...item,
              id: item.user_id, // Use user_id as the unique identifier
            });
          }
          
          return acc;
        }, []);
        
        setData(groupedData);
        setTotalRecords(groupedData.length);
      } else {
        message.error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'User ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 100,
      align: 'center',
    },
    {
      title: 'User Name',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 150,
      align: 'left',
    },
    {
      title: 'Month',
      dataIndex: 'report_month',
      key: 'report_month',
      width: 120,
      render: (month) => moment(month, 'YYYY-MM').format('MMM YYYY'),
      align: 'center',
    },
    {
      title: 'SMS Count',
      dataIndex: 'sms_count',
      key: 'sms_count',
      width: 120,
      render: (value) => (
        <span style={{ fontWeight: 'bold', fontSize: '14px' }} className="text-right">
          {formatIndianNumber(value)}
        </span>
      ),
      align: 'right',
      className: 'text-right',
      sorter: (a, b) => a.sms_count - b.sms_count,
    },
    {
      title: 'Sent',
      dataIndex: 'sent',
      key: 'sent',
      width: 110,
      render: (value) => formatIndianNumber(value),
      align: 'right',
    },
    {
      title: 'Delivered',
      dataIndex: 'delivered',
      key: 'delivered',
      width: 110,
      render: (value) => (
        <span style={{ color: '#52c41a' }}>
          {formatIndianNumber(value)}
        </span>
      ),
      align: 'right',
    },
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      width: 100,
      render: (value) => (
        <span style={{ color: value > 0 ? '#ff4d4f' : 'inherit' }}>
          {formatIndianNumber(value)}
        </span>
      ),
      align: 'right',
    },
    {
      title: 'Pending',
      dataIndex: 'pending',
      key: 'pending',
      width: 100,
      render: (value) => (
        <span style={{ color: '#faad14' }}>
          {formatIndianNumber(value)}
        </span>
      ),
      align: 'right',
    },
  ];

  const totalSMS = data.reduce((sum, item) => sum + (item.sms_count || 0), 0);
  const totalSent = data.reduce((sum, item) => sum + (item.sent || 0), 0);
  const totalDelivered = data.reduce((sum, item) => sum + (item.delivered || 0), 0);
  const totalFailed = data.reduce((sum, item) => sum + (item.failed || 0), 0);

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <span>
            Runtime SMS Count Dashboard
            <Tag color="blue" style={{ marginLeft: 10 }}>
              {selectedUser && selectedUser !== 'all' ? (users.find(u => u.user_id === selectedUser)?.user_name || `User ${selectedUser}`) : 'All Users'}
            </Tag>
            <Tag color="green" style={{ marginLeft: 5 }}>
              {moment(selectedMonth, 'YYYY-MM').format('MMMM YYYY')}
            </Tag>
          </span>
        }
        extra={
          <Space>
            <Select
              style={{ width: 150 }}
              value={selectedMonth}
              onChange={setSelectedMonth}
            >
              <Option key="month-2025-12" value="2025-12">December 2025</Option>
              <Option key="month-2025-11" value="2025-11">November 2025</Option>
              <Option key="month-2025-10" value="2025-10">October 2025</Option>
              <Option key="month-2025-09" value="2025-09">September 2025</Option>
              <Option key="month-2025-08" value="2025-08">August 2025</Option>
              <Option key="month-2025-07" value="2025-07">July 2025</Option>
              <Option key="month-2025-06" value="2025-06">June 2025</Option>
              <Option key="month-2025-05" value="2025-05">May 2025</Option>
            </Select>
            
            <Select
              style={{ width: 150 }}
              value={selectedUser}
              onChange={(value) => setSelectedUser(value)}
              placeholder="All Users"
              allowClear={false}
            >
              {users
                .filter(user => user.user_id !== null && user.user_id !== undefined)
                .map(user => (
                  <Option key={`user-${user.user_id}`} value={user.user_id}>
                    {user.user_name}
                  </Option>
                ))}
            </Select>
            
            <Button 
              type="primary" 
              icon={counting ? <StopOutlined /> : <PlayCircleOutlined />}
              loading={counting}
              onClick={handleRuntimeCount}
              danger={counting}
            >
              {counting ? 'Counting...' : 'Runtime Count'}
            </Button>
            
            <Button 
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={fetchData}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {/* Progress Bar */}
        {counting && (
          <div style={{ marginBottom: '16px' }}>
            <Progress 
              percent={progress} 
              status={progress === 100 ? 'success' : 'active'}
              format={percent => `${percent}%`}
            />
          </div>
        )}

        {/* Summary Statistics */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col span={6}>
            <Statistic
              title="Total SMS"
              value={totalSMS}
              formatter={(value) => formatIndianNumber(value)}
              styles={{ content: { color: '#1890ff' } }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Sent"
              value={totalSent}
              formatter={(value) => formatIndianNumber(value)}
              styles={{ content: { color: '#52c41a' } }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Delivered"
              value={totalDelivered}
              formatter={(value) => formatIndianNumber(value)}
              styles={{ content: { color: '#52c41a' } }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Total Failed"
              value={totalFailed}
              formatter={(value) => formatIndianNumber(value)}
              styles={{ content: { color: '#ff4d4f' } }}
            />
          </Col>
        </Row>

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={Array.isArray(data) ? data : []}
          rowKey={(record) => `${record.user_id}-${record.report_month}`}
          loading={loading}
          size="middle"
          scroll={{ x: 900, y: 400 }}
          pagination={{
            total: totalRecords,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
            pageSizeOptions: ['10', '20', '50', '100'],
            defaultPageSize: 20,
          }}
          summary={(pageData) => {
            let totalSMS = 0;
            let totalSent = 0;
            let totalDelivered = 0;
            let totalFailed = 0;
            let totalPending = 0;
            
            pageData.forEach(({ sms_count, sent, delivered, failed, pending }) => {
              totalSMS += sms_count || 0;
              totalSent += sent || 0;
              totalDelivered += delivered || 0;
              totalFailed += failed || 0;
              totalPending += pending || 0;
            });
            
            return (
              <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                <Table.Summary.Cell index={0}>
                  <span style={{ fontSize: '14px' }}>TOTAL ({pageData.length} records)</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <span>-</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <span>-</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span style={{ fontSize: '16px', color: '#1890ff', display: 'block', textAlign: 'right' }}>
                    {formatIndianNumber(totalSMS)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <span style={{ color: '#52c41a', display: 'block', textAlign: 'right' }}>
                    {formatIndianNumber(totalSent)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <span style={{ color: '#52c41a', display: 'block', textAlign: 'right' }}>
                    {formatIndianNumber(totalDelivered)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <span style={{ color: totalFailed > 0 ? '#ff4d4f' : '#52c41a', display: 'block', textAlign: 'right' }}>
                    {formatIndianNumber(totalFailed)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <span style={{ color: '#faad14', display: 'block', textAlign: 'right' }}>
                    {formatIndianNumber(totalPending)}
                  </span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default RuntimeCountDemo;
